import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  DeleteRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@mui/icons-material';
import type { FacultyMember } from '@dosfilos/domain';
import { safeImageUrl } from '../../../lib/featured-events';
import {
  MAX_FACULTY,
  MAX_FACULTY_BIO,
  initials,
  moveFaculty,
  newFacultyMember,
  parseAreas,
  validateFacultyMember,
  type FacultyError,
} from '../../../lib/landing-faculty';
import { LandingImageField } from './LandingImageField';

type Status = 'leadership' | 'visible' | 'hidden' | 'invalid';
const STATUS_COLOR: Record<Status, 'success' | 'info' | 'default' | 'warning'> = {
  leadership: 'success',
  visible: 'success',
  hidden: 'default',
  invalid: 'warning',
};

function statusOf(m: FacultyMember): Status {
  if (validateFacultyMember(m).length > 0) return 'invalid';
  if (!m.visible) return 'hidden';
  return m.leadership ? 'leadership' : 'visible';
}

/**
 * Editor del cuerpo académico del landing (Configuración → Landing). El orden
 * de la lista es el orden en la página; «Autoridad» sube a la fila destacada.
 */
export function FacultyEditor({
  tenantId,
  members,
  intro,
  onChange,
  onIntroChange,
}: {
  tenantId: string;
  members: FacultyMember[];
  intro: string;
  onChange: (next: FacultyMember[]) => void;
  onIntroChange: (next: string) => void;
}) {
  const { t } = useTranslation('sis');
  const [openId, setOpenId] = useState<string | null>(null);
  // Las áreas se editan como texto y se separan al salir del campo: si se
  // separaran en cada tecla, la coma recién escrita desaparecería.
  const [areasDraft, setAreasDraft] = useState<Record<string, string>>({});

  const patch = (id: string, change: Partial<FacultyMember>) =>
    onChange(members.map((m) => (m.id === id ? ({ ...m, ...change } as FacultyMember) : m)));

  const add = () => {
    const m = newFacultyMember(crypto.randomUUID());
    onChange([...members, m]);
    setOpenId(m.id);
  };

  const incomplete = members.some((m) => validateFacultyMember(m).length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {t('settings.landing.faculty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('settings.landing.faculty.description')}
      </Typography>

      <Stack spacing={1.5}>
        <TextField
          label={t('settings.landing.faculty.introLabel')}
          helperText={t('settings.landing.faculty.introHelper')}
          value={intro}
          onChange={(e) => onIntroChange(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={1}
        />

        {members.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('settings.landing.faculty.empty')}
          </Typography>
        )}

        {incomplete && <Alert severity="warning">{t('settings.landing.faculty.incomplete')}</Alert>}

        {members.map((m, idx) => {
          const open = openId === m.id;
          const errors = validateFacultyMember(m);
          const status = statusOf(m);
          const photo = safeImageUrl(m.photoUrl);
          const errorText = (field: FacultyError) =>
            errors.includes(field) ? t(`settings.landing.faculty.errors.${field}`) : undefined;
          const subtitle = [m.role.trim(), m.degree?.trim()].filter(Boolean).join(' · ');

          return (
            <Paper key={m.id} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 50,
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    flexShrink: 0,
                    bgcolor: '#161919',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'rgba(176,141,87,0.8)',
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontSize: 18,
                  }}
                >
                  {photo ? (
                    <Box
                      component="img"
                      src={photo}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    initials(m.name) || '·'
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {m.name.trim() || t('settings.landing.faculty.untitled')}
                  </Typography>
                  {subtitle && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {subtitle}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  color={STATUS_COLOR[status]}
                  label={t(`settings.landing.faculty.status.${status}`)}
                />
                <Tooltip title={t('settings.landing.faculty.moveUp')}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('settings.landing.faculty.moveUp')}
                      disabled={idx === 0}
                      onClick={() => onChange(moveFaculty(members, idx, -1))}
                    >
                      <ArrowUpwardRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('settings.landing.faculty.moveDown')}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('settings.landing.faculty.moveDown')}
                      disabled={idx === members.length - 1}
                      onClick={() => onChange(moveFaculty(members, idx, 1))}
                    >
                      <ArrowDownwardRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    open
                      ? t('settings.landing.faculty.collapse')
                      : t('settings.landing.faculty.expand')
                  }
                >
                  <IconButton
                    size="small"
                    aria-expanded={open}
                    aria-label={
                      open
                        ? t('settings.landing.faculty.collapse')
                        : t('settings.landing.faculty.expand')
                    }
                    onClick={() => setOpenId(open ? null : m.id)}
                  >
                    {open ? (
                      <ExpandLessRounded fontSize="small" />
                    ) : (
                      <ExpandMoreRounded fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('settings.landing.faculty.remove')}>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('settings.landing.faculty.remove')}
                    onClick={() => onChange(members.filter((x) => x.id !== m.id))}
                  >
                    <DeleteRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Collapse in={open} unmountOnExit>
                <Stack spacing={2} sx={{ p: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={m.visible}
                          onChange={(e) => patch(m.id, { visible: e.target.checked })}
                        />
                      }
                      label={t('settings.landing.faculty.visibleLabel')}
                    />
                    <Tooltip title={t('settings.landing.faculty.leadershipHelper')}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={m.leadership}
                            onChange={(e) => patch(m.id, { leadership: e.target.checked })}
                          />
                        }
                        label={t('settings.landing.faculty.leadershipLabel')}
                      />
                    </Tooltip>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label={t('settings.landing.faculty.nameLabel')}
                      placeholder={t('settings.landing.faculty.namePlaceholder')}
                      value={m.name}
                      onChange={(e) => patch(m.id, { name: e.target.value })}
                      size="small"
                      fullWidth
                      required
                      error={!!errorText('name')}
                      helperText={errorText('name')}
                    />
                    <TextField
                      label={t('settings.landing.faculty.roleLabel')}
                      placeholder={t('settings.landing.faculty.rolePlaceholder')}
                      value={m.role}
                      onChange={(e) => patch(m.id, { role: e.target.value })}
                      size="small"
                      fullWidth
                      required
                      error={!!errorText('role')}
                      helperText={errorText('role')}
                    />
                  </Stack>
                  <TextField
                    label={t('settings.landing.faculty.degreeLabel')}
                    placeholder={t('settings.landing.faculty.degreePlaceholder')}
                    value={m.degree ?? ''}
                    onChange={(e) => patch(m.id, { degree: e.target.value })}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label={t('settings.landing.faculty.areasLabel')}
                    helperText={t('settings.landing.faculty.areasHelper')}
                    value={areasDraft[m.id] ?? (m.areas ?? []).join(', ')}
                    onChange={(e) => setAreasDraft((d) => ({ ...d, [m.id]: e.target.value }))}
                    onBlur={(e) => {
                      patch(m.id, { areas: parseAreas(e.target.value) });
                      setAreasDraft((d) => {
                        const next = { ...d };
                        delete next[m.id];
                        return next;
                      });
                    }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label={t('settings.landing.faculty.bioLabel')}
                    value={m.bio ?? ''}
                    onChange={(e) => patch(m.id, { bio: e.target.value.slice(0, MAX_FACULTY_BIO) })}
                    helperText={t('settings.landing.faculty.bioHelper', {
                      count: (m.bio ?? '').length,
                      max: MAX_FACULTY_BIO,
                    })}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                  />
                  <LandingImageField
                    tenantId={tenantId}
                    fileStem={`faculty-${m.id}`}
                    value={m.photoUrl ?? ''}
                    onChange={(photoUrl) => patch(m.id, { photoUrl })}
                    i18nPrefix="settings.landing.faculty.photo"
                    aspectRatio="4 / 5"
                    previewWidth={140}
                  />
                </Stack>
              </Collapse>
            </Paper>
          );
        })}

        <Box>
          {members.length < MAX_FACULTY ? (
            <Button variant="outlined" size="small" startIcon={<AddRounded />} onClick={add}>
              {t('settings.landing.faculty.add')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('settings.landing.faculty.limit', { max: MAX_FACULTY })}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
